import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"

import type { AcceptedMemoryItem } from "@/api/home-context"
import type { M365ApprovalStatus, OnboardingStep, UserSettings } from "@/types/user-settings"

import {
  acceptOnboardingComplianceApi,
  completeOnboardingApi,
  forceResetOnboardingApi,
  updateOnboardingProgressApi,
} from "@/api/home-context"
import { ArrowLeft, CheckCircle2, GraduationCap, Loader2, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { useM365ApprovalGate } from "@/features/ai-explorer/hooks/use-m365-approval-gate"
import { hasCurrentM365Decision } from "@/features/ai-explorer/m365-approval"
import { useHomeContext } from "@/features/chat/hooks/use-home-context"
import { useConfigQuery } from "@/hooks/use-config-query"
import { isM365ToolEnabled } from "@/hooks/use-route-overrides"
import { useUserSettings } from "@/hooks/use-user-settings"
import { queryKeys } from "@/lib/query-keys"
import { gdprText } from "@/locales/constants"
import { SettingsService } from "@/services/settings-service"

import { OnboardingM365Step } from "../components/onboarding-m365-step"
import {
  OnboardingForceResetStatus,
  OnboardingModalShell,
  OnboardingProgressHeader,
} from "../components/onboarding-page-components"
import { OnboardingPersonalizationStep } from "../components/onboarding-personalization-step"
import { OnboardingWelcomeStep } from "../components/onboarding-welcome-step"
import { onboardingAboutMe } from "../onboarding-about-me"
import {
  cleanOnboardingMemoryFragment,
  selectOnboardingPeople,
} from "../onboarding-personalization-utils"
import {
  applyOnboardingCompletionToUserSettings,
  clearOnboardingCompletionReady,
  dismissSoftTrainingForSession,
  getOnboardingFlowSteps,
  holdOnboardingStep,
  isConfiguredAdminPath,
  latchOnboardingCompletionReady,
  readOnboardingCompletionReady,
  readSoftTrainingDismissed,
  resolveOnboardingStep,
  safeReturnTo,
  subscribeOnboardingCompletionReady,
} from "../onboarding-utils"

const MAX_ABOUT_ME_LENGTH = 4000

function settingsToken(): string | undefined {
  try {
    return SettingsService.getInstance().token
  } catch {
    return undefined
  }
}

export function OnboardingPage({
  defaultRoute,
  returnToOverride,
}: {
  defaultRoute: string
  returnToOverride?: string
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const settingsQuery = useUserSettings()
  const configQuery = useConfigQuery()
  const completionReady = useSyncExternalStore(
    subscribeOnboardingCompletionReady,
    readOnboardingCompletionReady,
    readOnboardingCompletionReady
  )

  const data = settingsQuery.data?.data
  const showCompleted = searchParams.get("completed") === "1" || completionReady
  const forceRequested =
    configQuery.data?.deploymentSlot === "test-2" && searchParams.get("forceOnboarding") === "1"
  const returnTo = safeReturnTo(
    returnToOverride || searchParams.get("returnTo") || data?.onboarding_progress?.return_to,
    defaultRoute
  )
  const adminExempt = isConfiguredAdminPath(configQuery.data?.frontend.routes, returnTo)
  const training = adminExempt
    ? { enabled: false, ai_awareness_completed: true, has_legacy_training: true }
    : configQuery.data?.training_gating
  const [softTrainingDismissed, setSoftTrainingDismissed] = useState(readSoftTrainingDismissed)
  const m365Enabled = isM365ToolEnabled()
  const resolution = resolveOnboardingStep({
    completedAt: data?.onboarding_completed_at,
    progress: data?.onboarding_progress,
    ui: data?.ui,
    training,
    showCompleted,
    softTrainingDismissed,
    m365Enabled,
  })
  const { decideM365 } = useM365ApprovalGate()
  const [m365Pending, setM365Pending] = useState<M365ApprovalStatus | null>(null)
  const [reviewStep, setReviewStep] = useState<{
    from: OnboardingStep
    step: OnboardingStep
  } | null>(null)
  // See holdOnboardingStep: stay on the live consent step (buttons busy) until
  // `decideM365` settles; on success the resolver advances against persisted
  // state, on failure the rollback keeps it here.
  const currentStep = holdOnboardingStep(resolution.step ?? "ready", {
    decisionPending: m365Pending !== null,
    reviewing: reviewStep !== null,
  })
  const showTrainingStep = training?.enabled === true && training.ai_awareness_completed !== true
  // Once the consent step has been shown in this wizard session it stays in the
  // progress header after the user decides, so the step numbers do not shift
  // under them. A resumed session with a decision already made never shows it.
  const [m365StepShown, setM365StepShown] = useState(false)
  useEffect(() => {
    if (currentStep === "m365") setM365StepShown(true)
  }, [currentStep])
  const showM365Step = m365Enabled && (!hasCurrentM365Decision(data?.ui) || m365StepShown)
  const visibleSteps = useMemo(
    () => getOnboardingFlowSteps(showTrainingStep, showM365Step),
    [showTrainingStep, showM365Step]
  )
  const displayedStep = reviewStep?.from === currentStep ? reviewStep.step : currentStep
  // Personalization is only displayed once `decideM365` has settled (see
  // `currentStep`), so this fetch always runs against the persisted decision
  // and after `clearHomeContext` dropped any cached empty graph.
  const homeContext = useHomeContext(displayedStep === "personalization")

  const [rememberPeople, setRememberPeople] = useState(false)
  const [rememberFiles, setRememberFiles] = useState(false)
  const [aboutMe, setAboutMe] = useState("")
  const aboutMeHydrated = useRef(false)
  const aboutMeDirty = useRef(false)
  const forceResetStarted = useRef(false)
  const stepPanelRef = useRef<HTMLElement>(null)
  const lastFocusedStep = useRef(displayedStep)

  useEffect(() => {
    if (lastFocusedStep.current === displayedStep) return
    lastFocusedStep.current = displayedStep
    const frame = window.requestAnimationFrame(() => {
      stepPanelRef.current?.querySelector<HTMLElement>("[data-onboarding-step-heading]")?.focus()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [displayedStep])

  useEffect(() => {
    if (currentStep !== "personalization") {
      aboutMeHydrated.current = false
      aboutMeDirty.current = false
      return
    }
    if (!homeContext.isSuccess || aboutMeHydrated.current || aboutMeDirty.current) return
    setAboutMe(homeContext.data.helloai.user_instructions ?? "")
    aboutMeHydrated.current = true
  }, [currentStep, homeContext.data?.helloai.user_instructions, homeContext.isSuccess])

  const progressMutation = useMutation({
    mutationFn: (step: OnboardingStep) =>
      updateOnboardingProgressApi(step, returnTo, settingsToken()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.userSettings })
    },
    onError: () => toast.error(t("personalization.smoothOnboarding.saveError")),
  })

  const completionMutation = useMutation({
    mutationFn: async (payload: {
      user_instructions?: string
      accepted_memories?: AcceptedMemoryItem[]
    }) => {
      const result = await completeOnboardingApi(payload, settingsToken())
      // Latch Ready in the same turn as completed_at so the coordinator cannot
      // treat the user as done (skip Ready) or fall through to personalization.
      latchOnboardingCompletionReady()
      queryClient.setQueryData<UserSettings>(queryKeys.userSettings, (current) =>
        applyOnboardingCompletionToUserSettings(current, result)
      )
      return result
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.userSettings }),
        queryClient.invalidateQueries({ queryKey: ["home-context"] }),
      ])
    },
    onError: () => toast.error(t("personalization.smoothOnboarding.completeError")),
  })

  const complianceMutation = useMutation({
    mutationFn: () => acceptOnboardingComplianceApi(settingsToken()),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.userSettings })
    },
    onError: () => toast.error(t("personalization.smoothOnboarding.saveError")),
  })

  const forceResetMutation = useMutation({
    mutationFn: () => forceResetOnboardingApi(settingsToken()),
    onSuccess: async () => {
      clearOnboardingCompletionReady()
      await queryClient.invalidateQueries({ queryKey: queryKeys.userSettings })
      void navigate(`/onboarding?returnTo=${encodeURIComponent(returnTo)}`, { replace: true })
    },
  })

  useEffect(() => {
    if (!forceRequested || forceResetStarted.current) return
    forceResetStarted.current = true
    forceResetMutation.mutate()
  }, [forceRequested, forceResetMutation])

  const profile = homeContext.data?.helloai.user_profile
  const people = useMemo(
    () => selectOnboardingPeople(homeContext.data?.graph.people ?? []),
    [homeContext.data]
  )
  const files = useMemo(
    () => homeContext.data?.graph.recent_files.slice(0, 3) ?? [],
    [homeContext.data]
  )

  async function acceptCompliance() {
    if (displayedStep !== currentStep) {
      setReviewStep(null)
      return
    }
    // Persist the next resumable step first. If the compliance write then
    // fails, the mandatory compliance resolver still keeps this page active;
    // if progress fails, we do not advance at all.
    await progressMutation.mutateAsync("training")
    await complianceMutation.mutateAsync()
  }

  async function startTraining() {
    setReviewStep(null)
    await progressMutation.mutateAsync("training")
    void navigate("/training?onboarding=1")
  }

  // Both choices persist a decision at the current consent version, so the
  // standalone chat-home prompt never re-asks after onboarding (Preferences
  // is the place to change it). Persist the decision before the resumable
  // step: if the decision write fails the user stays on this step and can
  // retry; if only the progress write fails, the resolver still moves on
  // because the decision is what drives this step.
  async function chooseM365(status: M365ApprovalStatus) {
    setM365Pending(status)
    try {
      await decideM365(status)
    } catch {
      // The settings mutation already rolled back and toasted; the rollback
      // puts the resolver back on this step so the user can retry.
      return
    } finally {
      setM365Pending(null)
    }
    if (displayedStep !== currentStep) {
      setReviewStep(null)
      return
    }
    await progressMutation.mutateAsync("personalization").catch(() => undefined)
  }

  async function deferTraining() {
    setReviewStep(null)
    dismissSoftTrainingForSession()
    setSoftTrainingDismissed(true)
    if (data?.onboarding_completed_at) {
      void navigate(returnTo, { replace: true })
      return
    }
    await progressMutation.mutateAsync("personalization")
  }

  function finishPersonalization() {
    const acceptedMemories: AcceptedMemoryItem[] = []
    if (rememberPeople) {
      for (const person of people) {
        const name = cleanOnboardingMemoryFragment(person.name, 80)
        if (!name) continue
        const title = cleanOnboardingMemoryFragment(person.job_title, 80)
        acceptedMemories.push({
          text: `Frequent collaborator: ${name}${title ? ` (${title})` : ""}`.slice(0, 200),
          type: "person",
        })
      }
    }
    if (rememberFiles) {
      for (const file of files) {
        const name = cleanOnboardingMemoryFragment(file.name, 120)
        if (!name) continue
        acceptedMemories.push({ text: `Working on: ${name}`.slice(0, 200), type: "project" })
      }
    }
    completionMutation.mutate({
      // Only typed, non-blank text: the prefill may be a cached or degraded read.
      ...onboardingAboutMe(aboutMe, aboutMeDirty.current),
      ...(acceptedMemories.length ? { accepted_memories: acceptedMemories } : {}),
    })
  }

  const busy =
    progressMutation.isPending ||
    complianceMutation.isPending ||
    completionMutation.isPending ||
    completionMutation.isSuccess ||
    m365Pending !== null
  const displayedStepIndex = visibleSteps.indexOf(displayedStep)
  const canGoBack =
    !data?.onboarding_completed_at &&
    displayedStep !== "welcome" &&
    displayedStep !== "ready" &&
    displayedStepIndex > 0

  function continueFromWelcome() {
    if (displayedStep !== currentStep) {
      setReviewStep(null)
      return
    }
    progressMutation.mutate("compliance")
  }

  function showPreviousStep() {
    const previousStep = visibleSteps[displayedStepIndex - 1]
    setReviewStep({ from: currentStep, step: previousStep })
  }

  if (forceRequested) {
    return (
      <OnboardingForceResetStatus
        error={forceResetMutation.error?.message ?? null}
        onRetry={() => {
          forceResetStarted.current = true
          forceResetMutation.mutate()
        }}
      />
    )
  }

  return (
    <OnboardingModalShell currentStep={displayedStep}>
      <OnboardingProgressHeader currentStep={displayedStep} steps={visibleSteps} />

      <section
        ref={stepPanelRef}
        aria-live="polite"
        className="bg-card min-h-0 rounded-xl p-4 sm:p-6"
        data-testid={`onboarding-step-${displayedStep}`}
      >
        {canGoBack && (
          <Button
            className="mb-4 min-h-10 px-2"
            disabled={busy}
            variant="ghost"
            onClick={showPreviousStep}
          >
            <ArrowLeft aria-hidden="true" className="size-4" />
            {t("personalization.smoothOnboarding.back")}
          </Button>
        )}

        {displayedStep === "welcome" && (
          <OnboardingWelcomeStep
            busy={busy}
            showM365Step={showM365Step}
            showTrainingStep={showTrainingStep}
            onStart={continueFromWelcome}
          />
        )}

        {displayedStep === "compliance" && (
          <div className="mx-auto max-w-3xl">
            <div className="flex items-start gap-4">
              <div className="bg-primary/10 text-primary flex size-12 shrink-0 items-center justify-center rounded-lg">
                <ShieldCheck aria-hidden="true" className="size-6" />
              </div>
              <div>
                <h1
                  data-onboarding-step-heading
                  className="font-display text-2xl font-semibold sm:text-3xl"
                  tabIndex={-1}
                >
                  {t("personalization.smoothOnboarding.compliance.title")}
                </h1>
                <p className="text-muted-foreground mt-2">
                  {t("personalization.smoothOnboarding.compliance.description")}
                </p>
              </div>
            </div>
            <div
              className="border-border bg-background mt-6 max-h-[48vh] overflow-y-auto rounded-lg border p-4 whitespace-pre-wrap sm:p-5"
              dangerouslySetInnerHTML={{ __html: gdprText[i18n.language] || gdprText.sv }}
            />
            <div className="mt-6 flex justify-end">
              <Button className="min-h-10" disabled={busy} onClick={() => void acceptCompliance()}>
                {busy && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
                {t("personalization.smoothOnboarding.compliance.accept")}
              </Button>
            </div>
          </div>
        )}

        {displayedStep === "training" && (
          <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
            <div className="bg-primary/10 text-primary flex size-16 items-center justify-center rounded-xl">
              <GraduationCap aria-hidden="true" className="size-8" />
            </div>
            <h1
              data-onboarding-step-heading
              className="font-display mt-5 text-2xl font-semibold sm:text-3xl"
              tabIndex={-1}
            >
              {t("personalization.smoothOnboarding.training.title")}
            </h1>
            <p className="text-muted-foreground mt-3 max-w-xl">
              {t("personalization.smoothOnboarding.training.description")}
            </p>
            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row">
              {!resolution.isMandatory && (
                <Button
                  className="min-h-10"
                  disabled={busy}
                  variant="outline"
                  onClick={() => void deferTraining()}
                >
                  {t("personalization.smoothOnboarding.training.later")}
                </Button>
              )}
              <Button className="min-h-10" disabled={busy} onClick={() => void startTraining()}>
                {busy && <Loader2 aria-hidden="true" className="size-4 animate-spin" />}
                {t("personalization.smoothOnboarding.training.start")}
              </Button>
            </div>
          </div>
        )}

        {displayedStep === "m365" && (
          <OnboardingM365Step
            busy={busy}
            pending={m365Pending}
            onDecide={(status) => void chooseM365(status)}
          />
        )}

        {displayedStep === "personalization" && (
          <OnboardingPersonalizationStep
            aboutMe={aboutMe}
            busy={busy}
            completionPending={completionMutation.isPending}
            files={files}
            homeContextError={homeContext.isError}
            maxAboutMeLength={MAX_ABOUT_ME_LENGTH}
            people={people}
            profile={profile}
            rememberFiles={rememberFiles}
            rememberPeople={rememberPeople}
            onContinue={finishPersonalization}
            onRememberFilesChange={setRememberFiles}
            onRememberPeopleChange={setRememberPeople}
            onSkip={() => completionMutation.mutate({})}
            onAboutMeChange={(value) => {
              aboutMeDirty.current = true
              setAboutMe(value)
            }}
          />
        )}

        {displayedStep === "ready" && (
          <div className="mx-auto flex max-w-2xl flex-col items-center text-center">
            <CheckCircle2 aria-hidden="true" className="text-success size-16" />
            <h1
              data-onboarding-step-heading
              className="font-display mt-5 text-3xl font-semibold"
              tabIndex={-1}
            >
              {t("personalization.smoothOnboarding.ready.title")}
            </h1>
            <p className="text-muted-foreground mt-3">
              {t("personalization.smoothOnboarding.ready.description")}
            </p>
            <Button
              className="mt-8 min-h-10"
              onClick={() => {
                clearOnboardingCompletionReady()
                void navigate(returnTo, { replace: true })
              }}
            >
              {t("personalization.smoothOnboarding.ready.openApp")}
            </Button>
          </div>
        )}
      </section>
    </OnboardingModalShell>
  )
}
